import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, {
  setAuthTokens,
  clearAuthTokens,
  getToken,
  getRefreshToken,
  apiErrorMessage,
  API,
} from "@/lib/api";
import axios from "axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, object=auth
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const accessToken = getToken();
    const refreshToken = getRefreshToken();

    // If no tokens exist at all
    if (!accessToken && !refreshToken) {
      setUser(false);
      setLoading(false);
      return;
    }

    // Attempt to verify current access token
    if (accessToken) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        setLoading(false);
        return;
      } catch (err) {
        // If 401, will fall through to refresh token attempt
        if (err?.response?.status !== 401) {
          clearAuthTokens();
          setUser(false);
          setLoading(false);
          return;
        }
      }
    }

    // Try silent refresh if refresh token is present
    if (refreshToken) {
      try {
        const { data } = await axios.post(
          `${API}/auth/refresh`,
          { refresh_token: refreshToken },
          { withCredentials: true }
        );
        setAuthTokens(data.token, data.refresh_token);
        setUser(data.user);
      } catch {
        clearAuthTokens();
        setUser(false);
      } finally {
        setLoading(false);
      }
      return;
    }

    clearAuthTokens();
    setUser(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();

    // Listen for global auth expiration event
    const handleAuthExpired = () => {
      setUser(false);
    };
    window.addEventListener("virality:auth:expired", handleAuthExpired);
    return () => window.removeEventListener("virality:auth:expired", handleAuthExpired);
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuthTokens(data.token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (name, email, password) => {
    // Legacy / direct register endpoint
    const { data } = await api.post("/auth/register", { name, email, password });
    setAuthTokens(data.token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const sendSignupOtp = async (name, email, password) => {
    const { data } = await api.post("/auth/signup/send-otp", { name, email, password });
    return data;
  };

  const verifySignupOtp = async (name, email, password, otp) => {
    const { data } = await api.post("/auth/signup/verify", { name, email, password, otp });
    setAuthTokens(data.token, data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const sendForgotPasswordOtp = async (email) => {
    const { data } = await api.post("/auth/forgot-password/send-otp", { email });
    return data;
  };

  const resetPassword = async (email, otp, newPassword) => {
    const { data } = await api.post("/auth/forgot-password/reset", {
      email,
      otp,
      new_password: newPassword,
    });
    setAuthTokens(data.token, data.refresh_token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    const refreshToken = getRefreshToken();
    try {
      await api.post("/auth/logout", { refresh_token: refreshToken });
    } catch {
      /* ignore */
    }
    clearAuthTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        sendSignupOtp,
        verifySignupOtp,
        sendForgotPasswordOtp,
        resetPassword,
        logout,
        apiErrorMessage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
